/**
 * @fileoverview Prompt template for chapter-restructure-v1.
 *
 * Restructures a subset of unwritten chapters: split, merge, or reorganise
 * their narrative scope into a new set of chapter outlines.
 */

export const prompt = `
You are a master story architect specialising in chapter structure and pacing.

## TASK INSTRUCTIONS:
Your task is to restructure the **selected chapters** listed under CHAPTERS TO RESTRUCTURE into a new set of chapter outlines. You may produce fewer chapters (merge/condense), more chapters (split/expand), or the same number with redistributed content — whatever best serves the narrative.

### Step 1: Understand the Full Picture
- Read the story context, existing chapter metadata, and any user guidance carefully.
- Understand where the selected chapters sit in the overall story arc. Which chapters come before and after them? What narrative threads flow through them?

### Step 2: Restructure
- Redistribute the narrative scope of the selected chapters based on the user's guidance and your editorial judgment.
- Ensure each new chapter has a clear purpose, strong pacing, and a compelling arc.
- Maintain continuity with the chapters that come before and after the restructured range.
- Do NOT alter or include chapters outside the selected range — only produce outlines for the chapters that replace the selected ones.

### Step 3: Verify Quality
- Each new chapter should have distinct, non-overlapping key events.
- Pacing should create a compelling rhythm across the restructured range.
- The total narrative scope of the new chapters should cover everything the original selected chapters covered — don't drop plot threads or events unless the user explicitly asks to.

<<<userMessage>>>

## PRIMARY CONTEXT

**CHAPTERS TO RESTRUCTURE**
The following chapters are your input. Restructure ONLY these chapters.
<<<chapterMetadata>>>

**FULL STORY OUTLINE & GOALS**
<<<storyContext>>>

<<<seriesContext>>>

## SECONDARY CONTEXT

<<<characterRoles>>>

<<<worldElements>>>

<<<loreContext>>>

---

## OUTPUT INSTRUCTIONS:

Headers should only contain the title of the chapter, don't output "Chapter X" or similar header content.

**Key Events (Seed)**: Write 1-2 sentences per chapter that summarise the core events. Use neutral, objective style — no dialogue, no sensory details. This seed will be expanded into detailed key events in a follow-up step, so focus on WHAT happens, not the detail of HOW.

**Chapter Goals**: For each goal field (plotDevelopment, charDevelopment, stakesAndTension), write 1-2 sentences that reference specific events from the chapter.

Provide your output in the required JSON format.
`;
